/**
 * BYK-31: Cabinet Hardware Animation Service
 *
 * Provides multi-part cabinet mesh creation and door/drawer animations.
 * Features:
 * - Multi-part meshes with separate body, doors, and drawers
 * - Smooth open/close animations with proper hinge rotation
 * - Click-to-toggle interaction
 * - Animation state management
 */

import * as BABYLON from "@babylonjs/core";
import type {
  CabinetAnimationState,
  CabinetAnimationConfig,
  CabinetPartState,
  CabinetLayoutType,
} from "@byk/shared";
import {
  DEFAULT_CABINET_ANIMATION_CONFIG,
  inferCabinetLayout,
} from "@byk/shared";

// =============================================================================
// Constants
// =============================================================================

const CM_TO_M = 0.01;

/** Material thickness for cabinet components (in meters) */
const PANEL_THICKNESS = 0.018; // 18mm panels
const DOOR_GAP = 0.002; // 2mm gap around doors
const HANDLE_SIZE = { width: 0.012, height: 0.1, depth: 0.025 }; // Handle dimensions

// =============================================================================
// Types
// =============================================================================

export interface CabinetMeshGroup {
  /** Root transform node for the entire cabinet */
  root: BABYLON.TransformNode;
  /** Cabinet body mesh (back, sides, top, bottom, shelves) */
  body: BABYLON.Mesh;
  /** Door meshes with their pivot points */
  doors: Map<string, { mesh: BABYLON.Mesh; pivot: BABYLON.TransformNode }>;
  /** Drawer meshes with their pivot points */
  drawers: Map<string, { mesh: BABYLON.Mesh; pivot: BABYLON.TransformNode }>;
  /** Handle meshes */
  handles: BABYLON.Mesh[];
  /** Internal shelf meshes (visible when doors open) */
  shelves: BABYLON.Mesh[];
}

export interface CreateCabinetOptions {
  volumeId: string;
  width: number; // cm
  height: number; // cm
  depth: number; // cm
  position: BABYLON.Vector3;
  rotation: number; // degrees
  scene: BABYLON.Scene;
  layout?: CabinetLayoutType;
  productName?: string;
  onPartClick?: (volumeId: string, partKey: string) => void;
}

// =============================================================================
// Animation State Store (Module-level)
// =============================================================================

const animationStates = new Map<string, CabinetAnimationState>();
const cabinetMeshes = new Map<string, CabinetMeshGroup>();

/**
 * Get animation state for a volume
 */
export function getAnimationState(volumeId: string): CabinetAnimationState | undefined {
  return animationStates.get(volumeId);
}

/**
 * Get cabinet mesh group for a volume
 */
export function getCabinetMeshGroup(volumeId: string): CabinetMeshGroup | undefined {
  return cabinetMeshes.get(volumeId);
}

/**
 * DEBUG: Get all cached cabinet IDs
 */
export function debugGetCacheKeys(): string[] {
  return Array.from(cabinetMeshes.keys());
}

/**
 * Initialize animation state for a cabinet
 */
function initAnimationState(volumeId: string, layout: CabinetLayoutType): CabinetAnimationState {
  const parts: Record<string, CabinetPartState> = {};

  switch (layout) {
    case "single_door":
      parts["door_single"] = { type: "door_single", openAmount: 0, isAnimating: false };
      break;
    case "double_door":
      parts["door_left"] = { type: "door_left", openAmount: 0, isAnimating: false };
      parts["door_right"] = { type: "door_right", openAmount: 0, isAnimating: false };
      break;
    case "single_drawer":
      parts["drawer_1"] = { type: "drawer", openAmount: 0, isAnimating: false };
      break;
    case "multi_drawer":
      parts["drawer_1"] = { type: "drawer", openAmount: 0, isAnimating: false };
      parts["drawer_2"] = { type: "drawer", openAmount: 0, isAnimating: false };
      parts["drawer_3"] = { type: "drawer", openAmount: 0, isAnimating: false };
      break;
    case "door_drawer":
      parts["door_single"] = { type: "door_single", openAmount: 0, isAnimating: false };
      parts["drawer_1"] = { type: "drawer", openAmount: 0, isAnimating: false };
      break;
    case "open_shelf":
      // No animated parts
      break;
  }

  const state: CabinetAnimationState = { volumeId, parts };
  animationStates.set(volumeId, state);
  return state;
}

// =============================================================================
// Mesh Creation
// =============================================================================

/**
 * Create a multi-part cabinet mesh with animated doors and drawers
 */
export function createCabinetMesh(options: CreateCabinetOptions): CabinetMeshGroup {
  const {
    volumeId,
    width,
    height,
    depth,
    position,
    rotation,
    scene,
    layout: layoutOverride,
    productName = "",
    onPartClick,
  } = options;

  // Convert to meters
  const w = width * CM_TO_M;
  const h = height * CM_TO_M;
  const d = depth * CM_TO_M;

  // Determine layout
  const layout = layoutOverride || inferCabinetLayout(width, height, productName);

  // Initialize animation state
  initAnimationState(volumeId, layout);

  // Create root transform node
  const root = new BABYLON.TransformNode(`cabinet-root-${volumeId}`, scene);
  root.position = position;
  root.rotation.y = (rotation * Math.PI) / 180;

  // Create materials
  const bodyMaterial = createCabinetMaterial(`body-mat-${volumeId}`, scene, "#F5F5F5");
  const doorMaterial = createCabinetMaterial(`door-mat-${volumeId}`, scene, "#D4C4B0"); // Warm wood tone
  const handleMaterial = createCabinetMaterial(`handle-mat-${volumeId}`, scene, "#333333", 0.8, 0.3);
  const interiorMaterial = createCabinetMaterial(`interior-mat-${volumeId}`, scene, "#E8E8E8");

  console.log(`[BYK-31] Creating cabinet materials for ${volumeId}, layout: ${layout}`);

  // Create cabinet body
  const body = createCabinetBody(volumeId, w, h, d, scene, bodyMaterial, interiorMaterial);
  body.parent = root;

  // Track created components
  const doors = new Map<string, { mesh: BABYLON.Mesh; pivot: BABYLON.TransformNode }>();
  const drawers = new Map<string, { mesh: BABYLON.Mesh; pivot: BABYLON.TransformNode }>();
  const handles: BABYLON.Mesh[] = [];
  const shelves: BABYLON.Mesh[] = [];

  // Create internal shelves
  const shelfCount = Math.floor(height / 30); // One shelf every ~30cm
  for (let i = 1; i < shelfCount; i++) {
    const shelfY = (h / shelfCount) * i - h / 2 + PANEL_THICKNESS;
    const shelf = BABYLON.MeshBuilder.CreateBox(
      `shelf-${volumeId}-${i}`,
      {
        width: w - PANEL_THICKNESS * 2,
        height: PANEL_THICKNESS / 2,
        depth: d - PANEL_THICKNESS - 0.02,
      },
      scene
    );
    shelf.position = new BABYLON.Vector3(0, shelfY, PANEL_THICKNESS / 2);
    shelf.material = interiorMaterial;
    shelf.parent = root;
    shelf.isPickable = false; // Allow clicks to pass through to doors
    shelves.push(shelf);
  }

  // Create doors/drawers based on layout
  switch (layout) {
    case "single_door": {
      const { door, pivot, handle } = createDoor(
        volumeId,
        "door_single",
        w - DOOR_GAP * 2,
        h - DOOR_GAP * 2,
        scene,
        doorMaterial,
        handleMaterial,
        "right", // Hinge on right side
        onPartClick
      );
      pivot.position = new BABYLON.Vector3(w / 2 - DOOR_GAP, 0, d / 2);
      pivot.parent = root;
      doors.set("door_single", { mesh: door, pivot });
      handles.push(handle);
      break;
    }

    case "double_door": {
      // Left door (hinge on left)
      const left = createDoor(
        volumeId,
        "door_left",
        w / 2 - DOOR_GAP * 1.5,
        h - DOOR_GAP * 2,
        scene,
        doorMaterial,
        handleMaterial,
        "left",
        onPartClick
      );
      left.pivot.position = new BABYLON.Vector3(-w / 2 + DOOR_GAP, 0, d / 2);
      left.pivot.parent = root;
      doors.set("door_left", { mesh: left.door, pivot: left.pivot });
      handles.push(left.handle);

      // Right door (hinge on right)
      const right = createDoor(
        volumeId,
        "door_right",
        w / 2 - DOOR_GAP * 1.5,
        h - DOOR_GAP * 2,
        scene,
        doorMaterial,
        handleMaterial,
        "right",
        onPartClick
      );
      right.pivot.position = new BABYLON.Vector3(w / 2 - DOOR_GAP, 0, d / 2);
      right.pivot.parent = root;
      doors.set("door_right", { mesh: right.door, pivot: right.pivot });
      handles.push(right.handle);
      break;
    }

    case "single_drawer": {
      const { drawer, pivot, handle } = createDrawer(
        volumeId,
        "drawer_1",
        w - DOOR_GAP * 2,
        h - DOOR_GAP * 2,
        d - PANEL_THICKNESS,
        scene,
        doorMaterial,
        handleMaterial,
        onPartClick
      );
      pivot.position = new BABYLON.Vector3(0, 0, 0);
      pivot.parent = root;
      drawers.set("drawer_1", { mesh: drawer, pivot });
      handles.push(handle);
      break;
    }

    case "multi_drawer": {
      const drawerHeight = (h - DOOR_GAP * 4) / 3;
      for (let i = 0; i < 3; i++) {
        const { drawer, pivot, handle } = createDrawer(
          volumeId,
          `drawer_${i + 1}`,
          w - DOOR_GAP * 2,
          drawerHeight - DOOR_GAP,
          d - PANEL_THICKNESS,
          scene,
          doorMaterial,
          handleMaterial,
          onPartClick
        );
        const yPos = -h / 2 + drawerHeight / 2 + drawerHeight * i + DOOR_GAP * (i + 1);
        pivot.position = new BABYLON.Vector3(0, yPos, 0);
        pivot.parent = root;
        drawers.set(`drawer_${i + 1}`, { mesh: drawer, pivot });
        handles.push(handle);
      }
      break;
    }

    case "door_drawer": {
      // Door on top (2/3 height)
      const doorHeight = h * 0.65 - DOOR_GAP * 2;
      const drawerHeight = h * 0.35 - DOOR_GAP * 2;

      const { door, pivot, handle: doorHandle } = createDoor(
        volumeId,
        "door_single",
        w - DOOR_GAP * 2,
        doorHeight,
        scene,
        doorMaterial,
        handleMaterial,
        "right",
        onPartClick
      );
      pivot.position = new BABYLON.Vector3(w / 2 - DOOR_GAP, h / 2 - doorHeight / 2 - DOOR_GAP, d / 2);
      pivot.parent = root;
      doors.set("door_single", { mesh: door, pivot });
      handles.push(doorHandle);

      // Drawer on bottom
      const { drawer, pivot: drawerPivot, handle: drawerHandle } = createDrawer(
        volumeId,
        "drawer_1",
        w - DOOR_GAP * 2,
        drawerHeight,
        d - PANEL_THICKNESS,
        scene,
        doorMaterial,
        handleMaterial,
        onPartClick
      );
      drawerPivot.position = new BABYLON.Vector3(0, -h / 2 + drawerHeight / 2 + DOOR_GAP, 0);
      drawerPivot.parent = root;
      drawers.set("drawer_1", { mesh: drawer, pivot: drawerPivot });
      handles.push(drawerHandle);
      break;
    }

    case "open_shelf":
      // No doors or drawers
      break;
  }

  const meshGroup: CabinetMeshGroup = {
    root,
    body,
    doors,
    drawers,
    handles,
    shelves,
  };

  cabinetMeshes.set(volumeId, meshGroup);
  return meshGroup;
}

/**
 * Create cabinet body (back, sides, top, bottom)
 */
function createCabinetBody(
  volumeId: string,
  w: number,
  h: number,
  d: number,
  scene: BABYLON.Scene,
  exteriorMat: BABYLON.Material,
  interiorMat: BABYLON.Material
): BABYLON.Mesh {
  // Create body as a box with interior visible
  const body = BABYLON.MeshBuilder.CreateBox(
    `cabinet-body-${volumeId}`,
    {
      width: w,
      height: h,
      depth: d,
    },
    scene
  );

  // Position body so front face is at z=d/2, bottom at y=-h/2
  body.position = new BABYLON.Vector3(0, 0, 0);
  body.material = exteriorMat;

  // Make body non-pickable so clicks can pass through to doors/drawers
  body.isPickable = false;

  // Create interior back panel (visible when doors open)
  const backPanel = BABYLON.MeshBuilder.CreatePlane(
    `cabinet-back-interior-${volumeId}`,
    {
      width: w - PANEL_THICKNESS * 2,
      height: h - PANEL_THICKNESS * 2,
    },
    scene
  );
  backPanel.position = new BABYLON.Vector3(0, 0, -d / 2 + PANEL_THICKNESS + 0.001);
  backPanel.material = interiorMat;
  backPanel.parent = body;
  backPanel.isPickable = false; // Interior panel, not interactive

  return body;
}

/**
 * Create a door with pivot point for hinge rotation
 */
function createDoor(
  volumeId: string,
  partKey: string,
  width: number,
  height: number,
  scene: BABYLON.Scene,
  doorMat: BABYLON.Material,
  handleMat: BABYLON.Material,
  hingeSide: "left" | "right",
  onPartClick?: (volumeId: string, partKey: string) => void
): { door: BABYLON.Mesh; pivot: BABYLON.TransformNode; handle: BABYLON.Mesh } {
  // Create pivot point at hinge location
  const pivot = new BABYLON.TransformNode(`door-pivot-${volumeId}-${partKey}`, scene);

  // Create door panel
  const door = BABYLON.MeshBuilder.CreateBox(
    `door-${volumeId}-${partKey}`,
    {
      width: width,
      height: height,
      depth: PANEL_THICKNESS,
    },
    scene
  );
  door.material = doorMat;

  // Position door relative to pivot (hinge)
  if (hingeSide === "left") {
    door.position = new BABYLON.Vector3(width / 2, 0, PANEL_THICKNESS / 2);
  } else {
    door.position = new BABYLON.Vector3(-width / 2, 0, PANEL_THICKNESS / 2);
  }
  door.parent = pivot;

  // Create handle
  const handle = BABYLON.MeshBuilder.CreateBox(
    `handle-${volumeId}-${partKey}`,
    {
      width: HANDLE_SIZE.width,
      height: HANDLE_SIZE.height,
      depth: HANDLE_SIZE.depth,
    },
    scene
  );
  handle.material = handleMat;

  // Position handle on opposite side of hinge
  const handleX = hingeSide === "left" ? width - 0.03 : -width + 0.03;
  handle.position = new BABYLON.Vector3(handleX, 0, PANEL_THICKNESS + HANDLE_SIZE.depth / 2);
  handle.parent = pivot;

  // Add click interaction
  if (onPartClick) {
    door.actionManager = new BABYLON.ActionManager(scene);
    door.actionManager.registerAction(
      new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPickTrigger, () => {
        onPartClick(volumeId, partKey);
      })
    );

    handle.actionManager = new BABYLON.ActionManager(scene);
    handle.actionManager.registerAction(
      new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPickTrigger, () => {
        onPartClick(volumeId, partKey);
      })
    );
  }

  return { door, pivot, handle };
}

/**
 * Create a drawer with slide animation support
 */
function createDrawer(
  volumeId: string,
  partKey: string,
  width: number,
  height: number,
  maxSlide: number,
  scene: BABYLON.Scene,
  faceMat: BABYLON.Material,
  handleMat: BABYLON.Material,
  onPartClick?: (volumeId: string, partKey: string) => void
): { drawer: BABYLON.Mesh; pivot: BABYLON.TransformNode; handle: BABYLON.Mesh } {
  // Create pivot point for translation
  const pivot = new BABYLON.TransformNode(`drawer-pivot-${volumeId}-${partKey}`, scene);

  // Create drawer front panel
  const drawer = BABYLON.MeshBuilder.CreateBox(
    `drawer-${volumeId}-${partKey}`,
    {
      width: width,
      height: height,
      depth: PANEL_THICKNESS,
    },
    scene
  );
  drawer.material = faceMat;
  drawer.position = new BABYLON.Vector3(0, 0, maxSlide / 2 + PANEL_THICKNESS / 2);
  drawer.parent = pivot;

  // Create drawer box (visible when open)
  const drawerBox = BABYLON.MeshBuilder.CreateBox(
    `drawer-box-${volumeId}-${partKey}`,
    {
      width: width - 0.02,
      height: height - 0.02,
      depth: maxSlide - 0.02,
    },
    scene
  );
  const boxMat = faceMat.clone(`drawer-box-mat-${volumeId}-${partKey}`);
  (boxMat as BABYLON.StandardMaterial).diffuseColor = new BABYLON.Color3(0.85, 0.85, 0.85);
  drawerBox.material = boxMat;
  drawerBox.position = new BABYLON.Vector3(0, 0, PANEL_THICKNESS / 2);
  drawerBox.parent = pivot;

  // Create handle
  const handle = BABYLON.MeshBuilder.CreateBox(
    `handle-${volumeId}-${partKey}`,
    {
      width: HANDLE_SIZE.height, // Horizontal handle for drawers
      height: HANDLE_SIZE.width,
      depth: HANDLE_SIZE.depth,
    },
    scene
  );
  handle.material = handleMat;
  handle.position = new BABYLON.Vector3(0, 0, maxSlide / 2 + PANEL_THICKNESS + HANDLE_SIZE.depth / 2);
  handle.parent = pivot;

  // Add click interaction
  if (onPartClick) {
    drawer.actionManager = new BABYLON.ActionManager(scene);
    drawer.actionManager.registerAction(
      new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPickTrigger, () => {
        onPartClick(volumeId, partKey);
      })
    );

    handle.actionManager = new BABYLON.ActionManager(scene);
    handle.actionManager.registerAction(
      new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPickTrigger, () => {
        onPartClick(volumeId, partKey);
      })
    );
  }

  return { drawer, pivot, handle };
}

/**
 * Create a PBR-like material for cabinet components
 */
function createCabinetMaterial(
  name: string,
  scene: BABYLON.Scene,
  color: string,
  metallic = 0,
  roughness = 0.7
): BABYLON.StandardMaterial {
  const mat = new BABYLON.StandardMaterial(name, scene);
  mat.diffuseColor = BABYLON.Color3.FromHexString(color);
  mat.specularColor = new BABYLON.Color3(0.2 * (1 - roughness), 0.2 * (1 - roughness), 0.2 * (1 - roughness));
  mat.specularPower = 64 * (1 - roughness);
  if (metallic > 0) {
    mat.specularColor = new BABYLON.Color3(0.5, 0.5, 0.5);
  }
  return mat;
}

// =============================================================================
// Animation Functions
// =============================================================================

/**
 * Toggle a cabinet part (door or drawer) open/closed
 */
export function toggleCabinetPart(
  volumeId: string,
  partKey: string,
  config: CabinetAnimationConfig = DEFAULT_CABINET_ANIMATION_CONFIG
): void {
  const state = animationStates.get(volumeId);
  const meshGroup = cabinetMeshes.get(volumeId);

  if (!state || !meshGroup) {
    return;
  }

  const partState = state.parts[partKey];
  if (!partState || partState.isAnimating) {
    return;
  }

  // Determine target state (open if currently less than halfway open)
  const isOpening = partState.openAmount < 0.5;

  // Get the relevant mesh pivot
  const doorData = meshGroup.doors.get(partKey);
  const drawerData = meshGroup.drawers.get(partKey);
  const pivot = doorData?.pivot || drawerData?.pivot;

  if (!pivot) {
    return;
  }

  // Mark as animating
  partState.isAnimating = true;

  // Get scene from pivot
  const scene = pivot.getScene();

  // Create animation
  const frameRate = 60;
  const totalFrames = Math.round((config.animationDuration / 1000) * frameRate);

  if (doorData) {
    // Door rotation animation
    animateDoorRotation(
      pivot,
      partState,
      partKey,
      isOpening,
      config.doorSwingAngle,
      totalFrames,
      frameRate,
      scene
    );
  } else if (drawerData) {
    // Drawer slide animation
    animateDrawerSlide(
      pivot,
      partState,
      isOpening,
      config.drawerSlideDistance * CM_TO_M,
      totalFrames,
      frameRate,
      scene
    );
  }
}

/**
 * Animate door rotation
 */
function animateDoorRotation(
  pivot: BABYLON.TransformNode,
  partState: CabinetPartState,
  partKey: string,
  isOpening: boolean,
  maxAngle: number,
  totalFrames: number,
  frameRate: number,
  scene: BABYLON.Scene
): void {
  const maxRad = (maxAngle * Math.PI) / 180;

  // Determine rotation direction based on hinge side
  const direction = partKey.includes("left") ? 1 : -1;
  const startAngle = partState.openAmount * maxRad * direction;
  const endAngle = isOpening ? maxRad * direction : 0;

  const anim = new BABYLON.Animation(
    `door-anim-${pivot.name}`,
    "rotation.y",
    frameRate,
    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  anim.setKeys([
    { frame: 0, value: startAngle },
    { frame: totalFrames, value: endAngle },
  ]);

  // Add easing
  const easingFunction = new BABYLON.CubicEase();
  easingFunction.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEINOUT);
  anim.setEasingFunction(easingFunction);

  pivot.animations = [anim];

  // Run the animation
  scene.beginDirectAnimation(
    pivot,
    [anim],
    0,
    totalFrames,
    false,
    1,
    () => {
      // Animation complete callback
      partState.openAmount = isOpening ? 1 : 0;
      partState.isAnimating = false;
    }
  );
}

/**
 * Animate drawer slide
 */
function animateDrawerSlide(
  pivot: BABYLON.TransformNode,
  partState: CabinetPartState,
  isOpening: boolean,
  maxSlide: number,
  totalFrames: number,
  frameRate: number,
  scene: BABYLON.Scene
): void {
  const startZ = partState.openAmount * maxSlide;
  const endZ = isOpening ? maxSlide : 0;

  const anim = new BABYLON.Animation(
    `drawer-anim-${pivot.name}`,
    "position.z",
    frameRate,
    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  anim.setKeys([
    { frame: 0, value: startZ },
    { frame: totalFrames, value: endZ },
  ]);

  // Add easing
  const easingFunction = new BABYLON.CubicEase();
  easingFunction.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEINOUT);
  anim.setEasingFunction(easingFunction);

  pivot.animations = [anim];

  scene.beginAnimation(pivot, 0, totalFrames, false, 1, () => {
    partState.openAmount = isOpening ? 1 : 0;
    partState.isAnimating = false;
  });
}

/**
 * Open all parts of a cabinet
 */
export function openAllParts(
  volumeId: string,
  config: CabinetAnimationConfig = DEFAULT_CABINET_ANIMATION_CONFIG
): void {
  const state = animationStates.get(volumeId);
  if (!state) return;

  Object.keys(state.parts).forEach((partKey) => {
    if (state.parts[partKey].openAmount < 0.5) {
      toggleCabinetPart(volumeId, partKey, config);
    }
  });
}

/**
 * Close all parts of a cabinet
 */
export function closeAllParts(
  volumeId: string,
  config: CabinetAnimationConfig = DEFAULT_CABINET_ANIMATION_CONFIG
): void {
  const state = animationStates.get(volumeId);
  if (!state) return;

  Object.keys(state.parts).forEach((partKey) => {
    if (state.parts[partKey].openAmount > 0.5) {
      toggleCabinetPart(volumeId, partKey, config);
    }
  });
}

// =============================================================================
// Cleanup
// =============================================================================

/**
 * Dispose of a cabinet mesh group and its animation state
 */
export function disposeCabinetMesh(volumeId: string): void {
  const meshGroup = cabinetMeshes.get(volumeId);
  if (meshGroup) {
    // Dispose all meshes
    meshGroup.root.dispose();
    cabinetMeshes.delete(volumeId);
  }
  animationStates.delete(volumeId);
}

/**
 * Dispose all cabinet meshes
 */
export function disposeAllCabinetMeshes(): void {
  cabinetMeshes.forEach((_, volumeId) => {
    disposeCabinetMesh(volumeId);
  });
}

/**
 * Check if a product should use cabinet animation (based on category)
 */
export function shouldUseCabinetAnimation(categoryName: string): boolean {
  const lowerName = categoryName.toLowerCase();
  return lowerName.includes("cabinet") || lowerName.includes("drawer") || lowerName.includes("storage");
}
